import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CreateRoleInputSchema, type CreateRoleInput } from '@magimp/shared';
import { useCreateRole } from '@magimp/shared-hooks';
import {
  EditorModal,
  Input,
  Textarea,
  Button,
  FormRow,
  useEditorGuard,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';

export interface RoleCreateModalProps {
  onClose: () => void;
}

export function RoleCreateModal({ onClose }: RoleCreateModalProps) {
  const { t } = useTranslation(['roles', 'common']);
  const create = useCreateRole();
  const showToast = useToastStore((s) => s.show);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<CreateRoleInput>({
    resolver: zodResolver(CreateRoleInputSchema),
    // Empty `description` is `undefined` so the schema's `string().min(1).optional()`
    // accepts a blank field. Surface coerces an empty Textarea back to undefined
    // before submission too; the duplicate guard makes intent explicit at the
    // mutate boundary even if the form ever wires up a different default.
    defaultValues: { name: '', displayName: '', description: undefined },
  });

  const onSubmit = (values: CreateRoleInput) => {
    const desc = typeof values.description === 'string' ? values.description.trim() : '';
    const payload: CreateRoleInput = {
      name: values.name,
      displayName: values.displayName,
      description: desc.length > 0 ? desc : undefined,
    };
    create.mutate(payload, {
      onSuccess: () => {
        // Reset baseline first so any re-render between toast + onClose does
        // not flag the form as dirty against the new (post-submit) baseline.
        reset({ name: '', displayName: '', description: undefined });
        showToast({ tone: 'success', text: t('roles:toasts.created') });
        onClose();
      },
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('roles:modals.createTitle')}
      width={460}
      dirty={isDirty}
      busy={create.isPending}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormRow label={t('roles:fields.identifierCreate')} error={errors.name?.message}>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                invalid={!!errors.name}
                placeholder="sales_manager"
              />
            )}
          />
        </FormRow>
        <FormRow label={t('roles:fields.displayName')} error={errors.displayName?.message}>
          <Controller
            control={control}
            name="displayName"
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                invalid={!!errors.displayName}
                placeholder={t('roles:fields.displayNamePlaceholder')}
              />
            )}
          />
        </FormRow>
        <FormRow label={t('roles:fields.descriptionOptional')}>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea value={field.value ?? ''} onChange={field.onChange} rows={3} />
            )}
          />
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
          <Button type="submit" kind="primary" disabled={create.isPending}>
            {t('common:actions.create')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

// WHY: Cancel is an in-form close affordance, not a Modal-chrome close path
// (X / Esc / backdrop) — those are handled by EditorModal internally. Route
// Cancel through `confirmDestructive` so a dirty form still prompts before
// discarding typed input.
function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
